import { FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import {
  AvailableGuardrailType,
  AvailableGuardrailTypeField,
  AvailableGuardrailTypeFieldType,
  AvailableGuardrailTypeHandlingMode,
  Validator,
} from '@vendasta/protos/ai_assistants/v1alpha1';
import { FormArrayWithDirtyTracking } from './forms';

export type GuardrailFieldValue = string | number | boolean | string[] | null;
export type GuardrailFieldsForm = FormGroup<Record<string, FormControl<GuardrailFieldValue>>>;

export type GuardrailFormControls = {
  name: FormControl<string>;
  handling: FormControl<AvailableGuardrailTypeHandlingMode | null>;
  blockedMessage: FormControl<string>;
  replacement: FormControl<string>;
  fields: GuardrailFieldsForm;
  instanceId: FormControl<string | null>;
  passthrough: FormControl<Validator | null>;
};

export interface GuardrailFormInit {
  handling?: AvailableGuardrailTypeHandlingMode | null;
  blockedMessage?: string;
  replacement?: string;
  fields?: Record<string, GuardrailFieldValue>;
  instanceId?: string;
  passthrough?: Validator;
}

type TypedConfig = NonNullable<Validator['typedConfig']>;
type Arm = TypedConfig['type'];
type Handling =
  | { type: 'warn'; warn: Record<string, never> }
  | { type: 'block'; block: { blockedMessage: string } }
  | { type: 'replace'; replace: { replacement: string } }
  | { type: 'redact'; redact: Record<string, never> };

const { HANDLING_MODE_WARN, HANDLING_MODE_BLOCK, HANDLING_MODE_REPLACE, HANDLING_MODE_REDACT } =
  AvailableGuardrailTypeHandlingMode;

// Validator type name → typed_config oneof arm. Proto structure, not a per-type form: a new
// validator type needs a new arm regardless. Its fields come from the types endpoint.
const VALIDATOR_ARMS: Record<string, Arm> = {
  safety_validator: 'safety',
  length_validator: 'length',
  profanity_filter: 'profanity',
  pii_redactor: 'pii',
  custom_rule_validator: 'customRule',
};

/** True when the proto has a typed_config arm for this validator type, so a row of it can be built and saved. */
export function isSupportedGuardrailType(name: string): boolean {
  return name in VALIDATOR_ARMS;
}

const RESERVED_ARM_KEYS = new Set(['handling', 'warn', 'block', 'replace', 'redact', 'instanceId']);

const MODE_BY_HANDLING: Record<Handling['type'], AvailableGuardrailTypeHandlingMode> = {
  warn: HANDLING_MODE_WARN,
  block: HANDLING_MODE_BLOCK,
  replace: HANDLING_MODE_REPLACE,
  redact: HANDLING_MODE_REDACT,
};

export class GuardrailForm extends FormGroup<GuardrailFormControls> {
  private appliedSpec: string | null = null;

  constructor(name: string, init: GuardrailFormInit = {}) {
    super({
      name: new FormControl(name, { nonNullable: true }),
      handling: new FormControl<AvailableGuardrailTypeHandlingMode | null>(init.handling ?? null),
      blockedMessage: new FormControl(init.blockedMessage ?? '', { nonNullable: true }),
      replacement: new FormControl(init.replacement ?? '', { nonNullable: true }),
      fields: new FormGroup(
        Object.fromEntries(
          Object.entries(init.fields ?? {}).map(([key, value]) => [key, new FormControl<GuardrailFieldValue>(value)]),
        ),
      ),
      instanceId: new FormControl<string | null>(init.instanceId ?? null),
      passthrough: new FormControl<Validator | null>(init.passthrough ?? null),
    });
  }

  get instanceId(): string | undefined {
    return this.controls.instanceId.value ?? undefined;
  }

  get passthrough(): Validator | undefined {
    return this.controls.passthrough.value ?? undefined;
  }

  static fromValidator(validator: Validator): GuardrailForm {
    const typed = validator.typedConfig;
    const arm = VALIDATOR_ARMS[validator.name];
    if (!typed || typed.type !== arm) {
      return new GuardrailForm(validator.name, { passthrough: validator });
    }
    const config = armConfig(typed);
    const handling = config['handling'] as Handling | undefined;
    const fields: Record<string, GuardrailFieldValue> = {};
    for (const [key, value] of Object.entries(config)) {
      if (RESERVED_ARM_KEYS.has(key)) continue;
      fields[toSnakeCase(key)] = (value ?? null) as GuardrailFieldValue;
    }
    const instanceId = config['instanceId'];
    // Warn is read as block: the panel never offers it, so a stored warn row is saved as block the next time.
    const mode = handling && handling.type !== 'warn' ? MODE_BY_HANDLING[handling.type] : HANDLING_MODE_BLOCK;
    return new GuardrailForm(validator.name, {
      handling: mode,
      blockedMessage: handling?.type === 'block' ? handling.block.blockedMessage : '',
      replacement: handling?.type === 'replace' ? handling.replace.replacement : '',
      fields,
      instanceId: typeof instanceId === 'string' && instanceId ? instanceId : undefined,
    });
  }

  static forType(spec: AvailableGuardrailType): GuardrailForm {
    if (!isSupportedGuardrailType(spec.name)) {
      throw new Error(`guardrail type ${spec.name} has no typed_config arm`);
    }
    const form = new GuardrailForm(spec.name, { handling: offeredHandlingModes(spec)[0] ?? null });
    form.applyTypeSpec(spec);
    return form;
  }

  applyTypeSpec(spec: AvailableGuardrailType): void {
    if (this.passthrough || this.appliedSpec === spec.name) return;
    if (spec.name !== this.controls.name.value) {
      throw new Error(`spec ${spec.name} applied to a ${this.controls.name.value} row`);
    }
    this.appliedSpec = spec.name;
    const fields = this.controls.fields;
    for (const field of spec.fields) {
      const existing = fields.controls[field.key];
      const control = existing ?? new FormControl<GuardrailFieldValue>(defaultValueFor(field.type));
      control.setValidators(fieldValidators(field));
      control.updateValueAndValidity({ emitEvent: false });
      if (!existing) {
        if (fields.disabled) control.disable({ emitEvent: false });
        fields.addControl(field.key, control, { emitEvent: false });
      }
    }
    fields.setValidators(spec.groupRules.map((rule) => atLeastOneOf(rule.atLeastOneOf)));
    fields.updateValueAndValidity({ emitEvent: false });
  }

  toValidator(): Validator {
    const passthrough = this.passthrough;
    if (passthrough) return passthrough;
    const name = this.controls.name.value;
    const arm = VALIDATOR_ARMS[name];
    const config: Record<string, unknown> = {};
    for (const [key, control] of Object.entries(this.controls.fields.controls)) {
      const value = control.getRawValue();
      if (value === null || value === undefined || value === '') continue;
      config[toCamelCase(key)] = value;
    }
    config['handling'] = this.handling();
    const instanceId = this.instanceId;
    if (instanceId) config['instanceId'] = instanceId;
    return { name, config: {}, typedConfig: { type: arm, [arm]: config } as unknown as TypedConfig };
  }

  private handling(): Handling {
    switch (this.controls.handling.value) {
      case HANDLING_MODE_BLOCK:
        return { type: 'block', block: { blockedMessage: this.controls.blockedMessage.value } };
      case HANDLING_MODE_REPLACE:
        return { type: 'replace', replace: { replacement: this.controls.replacement.value } };
      case HANDLING_MODE_REDACT:
        return { type: 'redact', redact: {} };
      default:
        return { type: 'warn', warn: {} };
    }
  }
}

export class AiValidatorsFormArray extends FormArrayWithDirtyTracking<GuardrailForm> {
  constructor(validators?: Validator[]) {
    super((validators ?? []).map((validator) => GuardrailForm.fromValidator(validator)));
  }

  toValidators(): Validator[] {
    return this.controls.map((form) => form.toValidator());
  }
}

/** The modes the panel offers: the endpoint's list without warn, which does nothing a customer can see. */
export function offeredHandlingModes(spec: AvailableGuardrailType): AvailableGuardrailTypeHandlingMode[] {
  return spec.handlingModes.filter(
    (mode) => mode !== AvailableGuardrailTypeHandlingMode.HANDLING_MODE_UNSPECIFIED && mode !== HANDLING_MODE_WARN,
  );
}

export function maxRunes(max: number): ValidatorFn {
  return (control) => {
    const value = control.value;
    if (typeof value !== 'string') return null;
    const actual = Array.from(value).length;
    return actual > max ? { maxRunes: { max, actual } } : null;
  };
}

export function atLeastOneOf(keys: string[]): ValidatorFn {
  return (group) => (keys.some((key) => hasValue(group.get(key)?.value)) ? null : { atLeastOneOf: keys });
}

const nonEmptyList: ValidatorFn = (control) =>
  Array.isArray(control.value) && control.value.length > 0 ? null : { required: true };

function fieldValidators(field: AvailableGuardrailTypeField): ValidatorFn[] {
  const validators: ValidatorFn[] = [];
  if (field.required) {
    validators.push(
      field.type === AvailableGuardrailTypeFieldType.FIELD_TYPE_STRING_LIST ? nonEmptyList : Validators.required,
    );
  }
  if (field.maxRunes > 0) validators.push(maxRunes(field.maxRunes));
  return validators;
}

function defaultValueFor(type: AvailableGuardrailTypeFieldType): GuardrailFieldValue {
  switch (type) {
    case AvailableGuardrailTypeFieldType.FIELD_TYPE_BOOL:
      return false;
    case AvailableGuardrailTypeFieldType.FIELD_TYPE_STRING_LIST:
      return [];
    case AvailableGuardrailTypeFieldType.FIELD_TYPE_STRING:
      return '';
    default:
      return null;
  }
}

function hasValue(value: unknown): boolean {
  return (
    value === true ||
    typeof value === 'number' ||
    (typeof value === 'string' && value !== '') ||
    (Array.isArray(value) && value.length > 0)
  );
}

function armConfig(typed: TypedConfig): Record<string, unknown> {
  return ((typed as unknown as Record<string, unknown>)[typed.type] as Record<string, unknown> | undefined) ?? {};
}

function toCamelCase(key: string): string {
  return key.replace(/_([a-z0-9])/g, (_, char: string) => char.toUpperCase());
}

function toSnakeCase(key: string): string {
  return key.replace(/[A-Z]/g, (char) => `_${char.toLowerCase()}`);
}
